import React from 'react';
import {NavLink} from "react-router-dom";
import {Disclosure} from '@headlessui/react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faUsers,
    faHandFist,
    faSitemap,
    faPersonRunning,
    faTags,
    faLayerGroup,
    faChartLine,
    faFilter,
    faUserGroup,
    faClipboardCheck,
    faCommentDots,
    faStar,
    faTicket,
    faChalkboardUser,
    faCalendarDays,
    faObjectGroup,
    faBoxOpen,
    faGift,
    faLock,
    faFileContract,
    faFileSignature,
    faCalendarCheck,
    faBoxesStacked,
    faReceipt,
    faCreditCard,
    faFileInvoiceDollar,
    faMoneyBillTransfer,
    faChevronDown,
    faFolderTree,
    faBagShopping,
    faIdCardClip,
    faStore
} from "@fortawesome/free-solid-svg-icons";

const NavItem = (props) => (
    <li>
        <NavLink
            to={props.to}
            className={({isActive}) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                        ? 'bg-pink-600 text-white shadow-sm'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`
            }
        >
            <FontAwesomeIcon icon={props.icon} className="w-4 opacity-80"/>
            <span className="truncate">{props.children}</span>
        </NavLink>
    </li>
);

const NavGroup = (props) => (
    <Disclosure defaultOpen={true}>
        {({open}) => (
            <div>
                <Disclosure.Button className="flex items-center justify-between w-full text-gray-400 hover:text-gray-200 text-xs font-semibold uppercase tracking-wider mb-1.5 px-2 transition-colors">
                    <span>{props.title}</span>
                    <FontAwesomeIcon icon={faChevronDown} className={`w-3 h-3 transition-transform ${open ? '' : '-rotate-90'}`}/>
                </Disclosure.Button>
                <Disclosure.Panel as="ul" className="space-y-0.5">
                    {props.children}
                </Disclosure.Panel>
            </div>
        )}
    </Disclosure>
);

// Groups the whole sidebar into the app's two real subsystems (customer
// management vs. booking system) so the nav mirrors that split now, ahead of
// the booking system eventually moving into its own repo/admin.
const SystemSection = (props) => (
    <div className="mb-7 last:mb-0 pb-7 last:pb-0 border-b last:border-b-0 border-gray-800/80">
        <div className="flex items-center gap-2.5 px-2 mb-4">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${props.accentClass}`}>
                <FontAwesomeIcon icon={props.icon} className="w-4 h-4"/>
            </div>
            <div className="min-w-0">
                <h2 className="text-white text-[13px] font-bold uppercase tracking-wide truncate">{props.title}</h2>
                <p className="text-gray-500 text-[11px] truncate">{props.subtitle}</p>
            </div>
        </div>
        <div className="space-y-5">
            {props.children}
        </div>
    </div>
);

const Sidebar = () => {
    return (
        <nav className="bg-gray-900 h-full w-64 flex-shrink-0 px-3 py-6 overflow-y-auto scrollbar-thin">
            <div className="flex items-center gap-2 mb-8 px-2">
                <div className="h-8 w-8 rounded-lg bg-pink-600 flex items-center justify-center text-white font-bold">P</div>
                <NavLink to='/'>
                    <span className="text-white text-lg font-bold tracking-tight">PegaSoft</span>
                </NavLink>
            </div>

            <SystemSection
                title="Customer management"
                subtitle="Figures, levels & customer data"
                icon={faIdCardClip}
                accentClass="bg-indigo-600"
            >
                <NavGroup title="Master data">
                    <NavItem to='/users' icon={faUsers}>Users</NavItem>
                    <NavItem to='/grips' icon={faHandFist}>Grips</NavItem>
                    <NavItem to='/figure-categories' icon={faSitemap}>Figure categories</NavItem>
                    <NavItem to='/figures' icon={faPersonRunning}>Figures</NavItem>
                    <NavItem to='/thematics' icon={faTags}>Thematics</NavItem>
                    <NavItem to='/levels' icon={faLayerGroup}>Levels</NavItem>
                </NavGroup>

                <NavGroup title="Live data">
                    <NavItem to='/customer-thematics' icon={faChartLine}>Figure progress tracker</NavItem>
                    <NavItem to='/customer-filters' icon={faFilter}>Customer filters</NavItem>
                    <NavItem to='/customers' icon={faUserGroup}>Customers</NavItem>
                    <NavItem to='/customer-level-requirement-summaries' icon={faClipboardCheck}>Customer level checker</NavItem>
                    <NavItem to='/all-customer-comments' icon={faCommentDots}>Customer comments</NavItem>
                </NavGroup>
            </SystemSection>

            <SystemSection
                title="Booking system"
                subtitle="Bookings, access & webshop"
                icon={faStore}
                accentClass="bg-pink-600"
            >
                <NavGroup title="Booking">
                    <NavItem to='/coupons' icon={faTicket}>Coupons</NavItem>
                    <NavItem to='/trainers' icon={faChalkboardUser}>Trainers</NavItem>
                    <NavItem to='/event-groups' icon={faCalendarDays}>Events</NavItem>
                    <NavItem to='/booking-groups' icon={faObjectGroup}>Booking groups</NavItem>
                    <NavItem to='/products' icon={faBoxOpen}>Products</NavItem>
                    <NavItem to='/product-free-pairs' icon={faGift}>Product free pairs</NavItem>
                    <NavItem to='/events' icon={faCalendarCheck}>Event List</NavItem>
                    <NavItem to='/customer-products' icon={faBoxesStacked}>Customer Products</NavItem>
                </NavGroup>

                <NavGroup title="Accounting">
                    <NavItem to='/orders' icon={faReceipt}>Sales</NavItem>
                    <NavItem to='/payments' icon={faCreditCard}>Payments</NavItem>
                    <NavItem to='/invoices' icon={faFileInvoiceDollar}>Invoices</NavItem>
                    <NavItem to='/payouts' icon={faMoneyBillTransfer}>Payouts</NavItem>
                </NavGroup>

                <NavGroup title="Smart lock">
                    <NavItem to='/smart-lock-permissions' icon={faLock}>Smart Lock Access</NavItem>
                    <NavItem to='/unattended-access-terms-versions' icon={faFileContract}>Unattended Access Terms</NavItem>
                    <NavItem to='/unattended-access-terms-acceptances' icon={faFileSignature}>Unattended Access Terms Acceptances</NavItem>
                </NavGroup>

                <NavGroup title="Webshop">
                    <NavItem to='/item-categories' icon={faFolderTree}>Item Categories</NavItem>
                    <NavItem to='/items' icon={faBagShopping}>Items</NavItem>
                </NavGroup>

                <NavGroup title="Marketing">
                    <NavItem to='/google-reviews' icon={faStar}>Google reviews</NavItem>
                </NavGroup>
            </SystemSection>
        </nav>
    );
};

export default Sidebar;
